import { Frequency } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Frequencies";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MedicationDosageDisplayMode, MedicationDosageDisplayModeDurationUnit } from "../Medication/MedicationModel";

class MedicalItemMedicationAndPrescriptionDosage {
    // Client side only -- Durée VS EndData prefered display Mode
    public DisplayMode: MedicationDosageDisplayMode = MedicationDosageDisplayMode.Duration;
    public DisplayModeDurationUnit: MedicationDosageDisplayModeDurationUnit = MedicationDosageDisplayModeDurationUnit.Days;

    public IsFakeDosage: boolean;
    public IfNeeded: boolean;
    public Id: string = GuidHelper.NewGuid();
    public DosageUnit: number;
    public DosageRoute: string;
    public DosageArrowIndicator: string;
    public DosageFrequency: string;
    public DosageString: string;
    public RankPosition: number;
    public DosageServedQuantity: string;
    public DosageServedDays: string;
    public DosageServedFrequency: string;
    public Duration?: number;
    public DosageTypeLookupId: string;
    public DosageTypeLookupValue: string;
    public MedicationId: string;

    constructor(init?: Partial<MedicalItemMedicationAndPrescriptionDosage>) {
        Object.assign(this, init);
    }

    public static calculateDispensationQuantity(dosage: MedicalItemMedicationAndPrescriptionDosage): number {
        if (!cacheStore.UnitsCache.firstOrDefault(p => p.Id === dosage.DosageUnit)?.IsTotalUnits)
            return null;

        const fields = new FieldsForDispensationCalculation(dosage);

        if (!fields.isValidForDispensationQuantityCalculation(dosage.DisplayMode))
            return null;

        const quantityResult = dosage.DisplayMode === MedicationDosageDisplayMode.Quantity
            ? Number(fields.dosageServedQuantity)
            : Number(fields.dose * fields.durationInDays / (fields.frequency.IntervalInDaysNumerator / fields.frequency.IntervalInDaysDenominator));

        // Rounds to the nearest quarter for values < 20, nearest half for values >= 20, and rounds up to the nearest integer for values >= 100.
        let roundingFactor = 4;

        if (quantityResult > 0 && quantityResult < 20)
            return Math.round(quantityResult * roundingFactor) / roundingFactor;

        if (quantityResult >= 20 && quantityResult < 100) {
            roundingFactor = 2;
            return Math.round(quantityResult * roundingFactor) / roundingFactor;
        }

        return Math.ceil(quantityResult);
    }

    public static calculateTotalDuration(dosage: MedicalItemMedicationAndPrescriptionDosage): number {
        if (!cacheStore.UnitsCache.firstOrDefault(p => p.Id === dosage.DosageUnit)?.IsTotalUnits)
            return null;

        const fields = new FieldsForDispensationCalculation(dosage);

        if (!fields.isValidForTotalDurationCalculation())
            return null;

        return Math.round(fields.dosageServedQuantity / (fields.dose / (fields.frequency.IntervalInDaysNumerator / fields.frequency.IntervalInDaysDenominator)));
    }

    public static getSelectedDosageTypeId(dosageTypeLookupId: string): string {
        return dosageTypeLookupId ? dosageTypeLookupId : cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType").LookupID;
    }

    public static getDurationValueInDays(value: string, unit: MedicationDosageDisplayModeDurationUnit): number {
        if (unit === MedicationDosageDisplayModeDurationUnit.Weeks) {
            return Number(value) * 7;
        }
        else if (unit === MedicationDosageDisplayModeDurationUnit.Months) {
            return DateTime.nowMoment().add(Number(value), "month").diff(DateTime.nowMoment(), "days");
        }
        else {
            return Number(value);
        }
    }
}

class FieldsForDispensationCalculation {
    private static readonly ticksPerDay: number = 864000000000;

    public durationInDays: number;
    public frequency: Frequency;
    public dose: number;
    public dosageServedQuantity: number;

    constructor(dosage: MedicalItemMedicationAndPrescriptionDosage) {
        const convertedDosageString: number = parseFloat((dosage?.DosageString ?? "").replace(",", "."));

        this.durationInDays = Math.round(dosage.Duration / FieldsForDispensationCalculation.ticksPerDay);
        this.frequency = cacheStore.FrequenciesCache.firstOrDefault(p => p.Code === dosage.DosageFrequency);
        this.dose = !isNaN(convertedDosageString) ? convertedDosageString : null;
        this.dosageServedQuantity = !isNaN(dosage.DosageServedQuantity as any) ? parseInt(dosage.DosageServedQuantity) : null;
    }

    public isValidForDispensationQuantityCalculation(displayMode: MedicationDosageDisplayMode): boolean {
        switch (displayMode) {
            case MedicationDosageDisplayMode.Quantity:
                return !isNaN(this.dosageServedQuantity);
            case MedicationDosageDisplayMode.Duration:
                return !isNaN(this.dose)
                    && this.durationInDays
                    && !isNaN(this.durationInDays)
                    && !!this.frequency?.IntervalInDaysNumerator
                    && !!this.frequency?.IntervalInDaysDenominator;
            default:
                return false;
        }
    }

    public isValidForTotalDurationCalculation(): boolean {
        if (isNaN(this.dose)
            || isNaN(this.dosageServedQuantity)
            || !this.frequency?.IntervalInDaysNumerator
            || !this.frequency?.IntervalInDaysDenominator
        )
            return false;

        return true;
    }
}

export { MedicalItemMedicationAndPrescriptionDosage };
